import React, { useState } from "react";
import Sidebar from "../components/Sidebar";
import {
  CalendarIcon,
  CheckCircle,
  XCircle,
  Moon,
  Sun,
  Search,
  Filter,
  Clock,
  Users,
  MapPin,
  Calendar,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import DateTimeHeader from "../components/DateTimeHeader";
import { useTheme } from "../contexts/ThemeContext";

const stats = [
  { label: "Scheduled Meetings", value: 26, color: "bg-blue-600" },
  { label: "Attended Meetings", value: 24, color: "bg-green-500" },
  { label: "Cancelled Meetings", value: 5, color: "bg-red-500" },
];

const dateFilterOptions = [
  { label: "Today", value: "today" },
  { label: "Yesterday", value: "yesterday" },
  { label: "Last 7 days", value: "7days" },
  { label: "Last 2 weeks", value: "14days" },
  { label: "Last month", value: "1month" },
  { label: "Last year", value: "1year" },
];

// Mock meeting history data
const mockMeetingHistory = [
  {
    id: 1,
    title: "Weekly Team Standup",
    date: "2024-01-15",
    time: "09:00 AM",
    duration: "30 min",
    status: "completed",
    attendees: 8,
    location: "Virtual",
    description:
      "Regular weekly team standup meeting to discuss progress and blockers.",
    organizer: "John Smith",
  },
  {
    id: 2,
    title: "Project Kickoff Meeting",
    date: "2024-01-14",
    time: "02:00 PM",
    duration: "1 hour",
    status: "completed",
    attendees: 12,
    location: "Conference Room A",
    description: "Kickoff meeting for the new Q1 project with stakeholders.",
    organizer: "Sarah Johnson",
  },
  {
    id: 3,
    title: "Client Presentation",
    date: "2024-01-13",
    time: "11:00 AM",
    duration: "45 min",
    status: "cancelled",
    attendees: 6,
    location: "Virtual",
    description: "Quarterly presentation to key client stakeholders.",
    organizer: "Mike Davis",
  },
  {
    id: 4,
    title: "Product Review Session",
    date: "2024-01-12",
    time: "03:30 PM",
    duration: "1.5 hours",
    status: "completed",
    attendees: 15,
    location: "Virtual",
    description: "Deep dive into product features and roadmap discussion.",
    organizer: "Lisa Chen",
  },
  {
    id: 5,
    title: "Budget Planning Meeting",
    date: "2024-01-11",
    time: "10:00 AM",
    duration: "2 hours",
    status: "completed",
    attendees: 10,
    location: "Board Room",
    description: "Annual budget planning and resource allocation discussion.",
    organizer: "David Wilson",
  },
  {
    id: 6,
    title: "Training Workshop",
    date: "2024-01-10",
    time: "01:00 PM",
    duration: "3 hours",
    status: "completed",
    attendees: 25,
    location: "Training Center",
    description: "New software training session for the development team.",
    organizer: "Emily Brown",
  },
];

const statusColors = {
  completed:
    "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  cancelled: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  scheduled: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
};

const statusIcons = {
  completed: <CheckCircle size={16} />,
  cancelled: <XCircle size={16} />,
  scheduled: <CalendarIcon size={16} />,
};

const Analytics: React.FC = () => {
  const {darkMode, toggleDarkMode} = useTheme();
  const [selectedDateFilter, setSelectedDateFilter] = useState("7days");
  const [collapsed, setCollapsed] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState("date");

  const handleStatClick = (
    statType: "scheduled" | "attended" | "cancelled"
  ) => {
    // Handle stat click - can be expanded later
    console.log("Stat clicked:", statType);
  };

  // Filter and search meetings
  const filteredMeetings = mockMeetingHistory
    .filter((meeting) => {
      const matchesSearch =
        meeting.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        meeting.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        meeting.organizer.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus =
        statusFilter === "all" || meeting.status === statusFilter;
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case "date":
          return new Date(b.date).getTime() - new Date(a.date).getTime();
        case "title":
          return a.title.localeCompare(b.title);
        case "duration":
          return parseInt(a.duration) - parseInt(b.duration);
        case "attendees":
          return b.attendees - a.attendees;
        default:
          return 0;
      }
    });

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <div
      className={`min-h-screen ${
        darkMode ? "bg-[#1a1a2e] text-white" : "bg-gray-50 text-gray-900"
      }`}
    >
      <div className="flex">
        <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />

        <main
          className={`flex-1 transition-all duration-300 ${
            collapsed ? "ml-20" : "ml-20 md:ml-58"
          }`}
        >
          {/* Header */}
          <div
            className={`sticky top-0 z-40 ${
              darkMode ? "bg-[#1a1a2e]/80" : "bg-white/80"
            } backdrop-blur-lg border-b ${
              darkMode ? "border-white/10" : "border-gray-200"
            }`}
          >
            <div className="flex items-center justify-between p-6">
              <div className="flex items-center gap-4">
                <h1
                  className={`text-2xl font-bold ${
                    darkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Analytics
                </h1>
              </div>

              <div className="flex items-center gap-4">
                <DateTimeHeader darkMode={darkMode} />

                {/* Dark Mode Toggle */}
                <button
                  onClick={toggleDarkMode}
                  className={`p-2 rounded-lg transition-all duration-200 ${
                    darkMode
                      ? "bg-white/10 text-white hover:bg-white/20"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {darkMode ? <Sun size={20} /> : <Moon size={20} />}
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Meeting Overview */}
            <div className="mb-8">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-1 h-6 bg-gradient-to-b from-pink-600 to-red-500 rounded-full"></div>
                  <h3
                    className={`text-xl font-bold ${
                      darkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    Meeting Overview
                  </h3>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`text-sm ${
                      darkMode ? "text-white/60" : "text-gray-600"
                    }`}
                  >
                    Filter by:
                  </span>
                  <div className="flex gap-1 p-1 rounded-lg bg-white/5 border border-white/10">
                    {dateFilterOptions.slice(0, 3).map((option) => (
                      <button
                        key={option.value}
                        className={`px-3 py-1 rounded-md text-xs font-medium transition-all duration-200 ${
                          selectedDateFilter === option.value
                            ? "bg-gradient-to-r from-pink-600 to-red-500 text-white shadow-sm"
                            : darkMode
                            ? "text-white/60 hover:text-white hover:bg-white/10"
                            : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                        }`}
                        onClick={() => setSelectedDateFilter(option.value)}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Modern Stats Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {stats.map((stat, index) => (
                  <div
                    key={stat.label}
                    className={`group relative overflow-hidden rounded-2xl p-6 border transition-all duration-300 hover:scale-[1.02] cursor-pointer ${
                      darkMode
                        ? "bg-white/5 border-white/10 hover:border-white/20 hover:bg-white/10"
                        : "bg-white border-gray-200 hover:border-gray-300 hover:bg-gray-50 shadow-sm"
                    }`}
                    onClick={() =>
                      handleStatClick(
                        index === 0
                          ? "scheduled"
                          : index === 1
                          ? "attended"
                          : "cancelled"
                      )
                    }
                  >
                    {/* Background Pattern */}
                    <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                    {/* Content */}
                    <div className="relative z-10">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-12 h-12 flex items-center justify-center rounded-xl ${stat.color} shadow-lg group-hover:scale-110 transition-transform duration-300`}
                          >
                            {index === 0 && (
                              <CalendarIcon size={24} className="text-white" />
                            )}
                            {index === 1 && (
                              <CheckCircle size={24} className="text-white" />
                            )}
                            {index === 2 && (
                              <XCircle size={24} className="text-white" />
                            )}
                          </div>
                          <div>
                            <div
                              className={`text-3xl font-bold ${
                                darkMode ? "text-white" : "text-gray-900"
                              }`}
                            >
                              {stat.value}
                            </div>
                            <div
                              className={`text-sm ${
                                darkMode ? "text-white/60" : "text-gray-500"
                              }`}
                            >
                              {stat.label}
                            </div>
                          </div>
                        </div>
                        <div
                          className={`text-right ${
                            darkMode ? "text-white/40" : "text-gray-400"
                          }`}
                        >
                          <div className="text-xs">Click to view</div>
                          <div className="text-xs">details</div>
                        </div>
                      </div>

                      {/* Progress Bar */}
                      <div className="w-full bg-gray-200 dark:bg-white/10 rounded-full h-2 mb-3">
                        <div
                          className={`h-2 rounded-full transition-all duration-500 ${stat.color}`}
                          style={{ width: `${(stat.value / 30) * 100}%` }}
                        ></div>
                      </div>

                      {/* Status Text */}
                      <div
                        className={`text-xs ${
                          darkMode ? "text-white/50" : "text-gray-500"
                        }`}
                      >
                        {index === 0 &&
                          `${stat.value} meetings in your calendar`}
                        {index === 1 &&
                          `${stat.value} meetings completed successfully`}
                        {index === 2 && `${stat.value} meetings were cancelled`}
                      </div>
                    </div>

                    {/* Corner Accent */}
                    <div
                      className={`absolute top-0 right-0 w-24 h-24 ${stat.color} opacity-10 rounded-full -translate-y-12 translate-x-12`}
                    ></div>
                  </div>
                ))}
              </div>

              {/* Quick Stats Summary */}
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div
                  className={`p-4 rounded-xl border ${
                    darkMode
                      ? "bg-white/5 border-white/10"
                      : "bg-white border-gray-200 shadow-sm"
                  }`}
                >
                  <div
                    className={`text-lg font-semibold ${
                      darkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    85%
                  </div>
                  <div
                    className={`text-xs ${
                      darkMode ? "text-white/60" : "text-gray-500"
                    }`}
                  >
                    Attendance Rate
                  </div>
                </div>
                <div
                  className={`p-4 rounded-xl border ${
                    darkMode
                      ? "bg-white/5 border-white/10"
                      : "bg-white border-gray-200 shadow-sm"
                  }`}
                >
                  <div
                    className={`text-lg font-semibold ${
                      darkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    12
                  </div>
                  <div
                    className={`text-xs ${
                      darkMode ? "text-white/60" : "text-gray-500"
                    }`}
                  >
                    This Week
                  </div>
                </div>
                <div
                  className={`p-4 rounded-xl border ${
                    darkMode
                      ? "bg-white/5 border-white/10"
                      : "bg-white border-gray-200 shadow-sm"
                  }`}
                >
                  <div
                    className={`text-lg font-semibold ${
                      darkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    3.2h
                  </div>
                  <div
                    className={`text-xs ${
                      darkMode ? "text-white/60" : "text-gray-500"
                    }`}
                  >
                    Avg Duration
                  </div>
                </div>
                <div
                  className={`p-4 rounded-xl border ${
                    darkMode
                      ? "bg-white/5 border-white/10"
                      : "bg-white border-gray-200 shadow-sm"
                  }`}
                >
                  <div
                    className={`text-lg font-semibold ${
                      darkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    92%
                  </div>
                  <div
                    className={`text-xs ${
                      darkMode ? "text-white/60" : "text-gray-500"
                    }`}
                  >
                    On Time
                  </div>
                </div>
              </div>
            </div>

            {/* Meeting History Section */}
            <div
              className={`rounded-2xl border ${
                darkMode
                  ? "bg-white/5 border-white/10"
                  : "bg-white border-gray-200 shadow-sm"
              }`}
            >
              <div className="p-6 border-b border-gray-200 dark:border-white/10">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-1 h-6 bg-gradient-to-b from-blue-600 to-purple-500 rounded-full"></div>
                    <h3
                      className={`text-xl font-bold ${
                        darkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      Meeting History
                    </h3>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        darkMode
                          ? "bg-white/10 text-white/60"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {filteredMeetings.length} meetings
                    </span>
                  </div>
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      darkMode
                        ? "bg-white/10 text-white hover:bg-white/20"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    <Filter size={16} />
                    Filters
                    {showFilters ? (
                      <ChevronUp size={16} />
                    ) : (
                      <ChevronDown size={16} />
                    )}
                  </button>
                </div>

                {/* Search and Filters */}
                <div className="flex flex-col lg:flex-row gap-4">
                  <div className="flex-1 relative">
                    <Search
                      size={20}
                      className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${
                        darkMode ? "text-white/40" : "text-gray-400"
                      }`}
                    />
                    <input
                      type="text"
                      placeholder="Search meetings by title, description, or organizer..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className={`w-full pl-10 pr-4 py-2 rounded-lg border transition-all duration-200 ${
                        darkMode
                          ? "bg-white/10 border-white/20 text-white placeholder-white/40 focus:border-white/40 focus:bg-white/20"
                          : "bg-white border-gray-200 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                      }`}
                    />
                  </div>

                  {showFilters && (
                    <div className="flex flex-wrap gap-3">
                      <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className={`px-3 py-2 rounded-lg border text-sm transition-all duration-200 ${
                          darkMode
                            ? "bg-white/10 border-white/20 text-white focus:border-white/40"
                            : "bg-white border-gray-200 text-gray-900 focus:border-blue-500"
                        }`}
                      >
                        <option value="all">All Status</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                        <option value="scheduled">Scheduled</option>
                      </select>

                      <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className={`px-3 py-2 rounded-lg border text-sm transition-all duration-200 ${
                          darkMode
                            ? "bg-white/10 border-white/20 text-white focus:border-white/40"
                            : "bg-white border-gray-200 text-gray-900 focus:border-blue-500"
                        }`}
                      >
                        <option value="date">Sort by Date</option>
                        <option value="title">Sort by Title</option>
                        <option value="duration">Sort by Duration</option>
                        <option value="attendees">Sort by Attendees</option>
                      </select>
                    </div>
                  )}
                </div>
              </div>

              {/* Meeting List */}
              <div className="p-6">
                {filteredMeetings.length === 0 ? (
                  <div
                    className={`text-center py-12 ${
                      darkMode ? "text-white/60" : "text-gray-500"
                    }`}
                  >
                    <CalendarIcon
                      size={48}
                      className="mx-auto mb-4 opacity-50"
                    />
                    <p className="text-lg font-medium mb-2">
                      No meetings found
                    </p>
                    <p className="text-sm">
                      Try adjusting your search or filter criteria
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {filteredMeetings.map((meeting) => (
                      <div
                        key={meeting.id}
                        className={`group p-4 rounded-xl border transition-all duration-200 hover:scale-[1.01] cursor-pointer ${
                          darkMode
                            ? "bg-white/5 border-white/10 hover:border-white/20 hover:bg-white/10"
                            : "bg-white border-gray-200 hover:border-gray-300 hover:bg-gray-50 shadow-sm"
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h4
                                className={`font-semibold ${
                                  darkMode ? "text-white" : "text-gray-900"
                                }`}
                              >
                                {meeting.title}
                              </h4>
                              <span
                                className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${
                                  statusColors[
                                    meeting.status as keyof typeof statusColors
                                  ]
                                }`}
                              >
                                {
                                  statusIcons[
                                    meeting.status as keyof typeof statusIcons
                                  ]
                                }
                                {meeting.status.charAt(0).toUpperCase() +
                                  meeting.status.slice(1)}
                              </span>
                            </div>

                            <p
                              className={`text-sm mb-3 ${
                                darkMode ? "text-white/60" : "text-gray-600"
                              }`}
                            >
                              {meeting.description}
                            </p>

                            <div className="flex flex-wrap items-center gap-4 text-xs">
                              <div
                                className={`flex items-center gap-1 ${
                                  darkMode ? "text-white/50" : "text-gray-500"
                                }`}
                              >
                                <Calendar size={14} />
                                {formatDate(meeting.date)}
                              </div>
                              <div
                                className={`flex items-center gap-1 ${
                                  darkMode ? "text-white/50" : "text-gray-500"
                                }`}
                              >
                                <Clock size={14} />
                                {meeting.time} ({meeting.duration})
                              </div>
                              <div
                                className={`flex items-center gap-1 ${
                                  darkMode ? "text-white/50" : "text-gray-500"
                                }`}
                              >
                                <Users size={14} />
                                {meeting.attendees} attendees
                              </div>
                              <div
                                className={`flex items-center gap-1 ${
                                  darkMode ? "text-white/50" : "text-gray-500"
                                }`}
                              >
                                <MapPin size={14} />
                                {meeting.location}
                              </div>
                            </div>
                          </div>

                          <div
                            className={`text-right ${
                              darkMode ? "text-white/40" : "text-gray-400"
                            }`}
                          >
                            <div className="text-xs mb-1">Organized by</div>
                            <div className="text-sm font-medium">
                              {meeting.organizer}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Analytics;
